import { CollapsibleRailShellModule } from './components/collapsible-rail-shell/collapsible-rail-shell.module';
// Common
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';

// Modules
import { OverlayModule } from '@angular/cdk/overlay';
import { AdminSideBarComponent } from '@modules/admin/components/side-bar/side-bar.component';
import { AttachmentsModule } from '@modules/attachments/attachments.module';
import { CalendarAppModule } from '@modules/calendar-app/calendar-app.module';
import { ConnectionsModule } from '@modules/connections/connections.module';
import { ContactsModule } from '@modules/contacts/contacts.module';
import { ElementsModule } from '@modules/elements/elements.module';
import { FilesModule } from '@modules/files/files.module';
import { KnotsModule } from '@modules/knots/knots.module';
import { MessagesModule } from '@modules/messages/messages.module';
import { NotesModule } from '@modules/notes/notes.module';
import { SheetsModule } from '@modules/sheets/sheets.module';
import { NotificationModule } from '@modules/notification/notification.module';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { ContextMenuItemComponent } from '@frontendlib/components/features/popover/components/context-menu-item/context-menu-item.component';
import { ContextMenuWindowComponent } from '@frontendlib/components/features/popover/components/context-menu-window/context-menu-window.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import { SearchModule } from '@modules/search/search.module';
import { SplitViewModule } from '@modules/split-view/split-view.module';
import { TagsModule } from '@modules/tags/tags.module';
import { TasksModule } from '@modules/tasks/tasks.module';
import { UtilsModule } from '@modules/utils/utils.module';
import { CommonModule as AppCommonModule } from '../common/common.module';
import { DynamicPanelModule } from '../dynamic-panel/dynamic-panel.module';
import { PagesRoutingModule } from './pages-routing.module';

// Components
import { DateRangeCalendarEventsStoreProviderComponent } from '@modules/calendar-app/components/events-list-container/date-range-calendar-events-store-provider.component';
import { CalendarAppContextDirective } from '@modules/calendar-app/directives/app-calendar-app-context.directive';
import { NavbarAiUsageRingComponent } from '@modules/common/components/navbar-ai-usage-ring/navbar-ai-usage-ring.component';
import { SidebarItemComponent } from '@modules/common/components/sidebar-item/sidebar-item.component';
import { ContactsAppContextDirective } from '@modules/contacts/directives/app-contacts-app-context.directive';
import { FilesAppContextDirective } from '@modules/files/directives/app-files-app-context.directive';
import { MatIconModule } from '@angular/material/icon';
import { MailAppContextDirective } from '@modules/messages/directives/app-mail-app-context.directive';
import { NotesAppContextDirective } from '@modules/notes/directives/app-notes-app-context.directive';
import { RailSkeletonComponent } from '@modules/common/components/rail-skeleton/rail-skeleton.component';
import { SearchPopupComponent } from '@modules/common/components/search-popup/search-popup.component';
import { SheetsAppContextDirective } from '@modules/sheets/directives/app-sheets-app-context.directive';
import { SheetsExpandedViewComponent } from '@modules/sheets/components/sheets-expanded-view/sheets-expanded-view.component';
import { SearchInputComponent } from '@modules/search/components/search-input/search-input.component';
import { SettingsSideBarComponent } from '@modules/settings/components/side-bar/side-bar.component';
import { TaskingAppContextDirective } from '@modules/tasks/directives/app-tasking-app-context.directive';
import { AdminLayoutComponent } from './components/admin-layout/admin-layout.component';
import { BaseNavBarComponent } from './components/base-nav-bar/base-nav-bar.component';
import { ContactsLayoutComponent } from './components/contacts-layout/contacts-layout.component';
import { EventsLayoutComponent } from './components/events-layout/events-layout.component';
import { FilesLayoutComponent } from './components/files-layout/files-layout.component';
import { LayoutComponent } from './components/layout/layout.component';
import { MailLayoutComponent } from './components/mail-layout/mail-layout.component';
import { NavBarComponent } from './components/nav-bar/nav-bar.component';
import { TeamUpdatesEntryComponent } from '@modules/team-updates/team-updates-entry.component';
import { TeamItemLinkOpenerComponent } from '@modules/team-item-link/team-item-link-opener.component';
import { RailShellComponent } from './components/rail-shell/rail-shell.component';
import { NavAddMenuComponent } from './components/nav-add-menu/nav-add-menu.component';
import { NotesLayoutComponent } from './components/notes-layout/notes-layout.component';
import { AtlasLayoutComponent } from './components/atlas-layout/atlas-layout.component';
import { AtlasProtoPartComponent } from './components/atlas-proto/atlas-proto-part.component';
import { SheetsLayoutComponent } from './components/sheets-layout/sheets-layout.component';
import { WorkbookPreviewComponent } from '@modules/sheets/workbook-preview.component';
import { ImportDialogComponent } from '@modules/sheets/import-dialog.component';
import { TemplateChooserComponent } from '@modules/sheets/template-chooser.component';
import { SettingsLayoutComponent } from './components/settings-layout/settings-layout.component';
import { SharedLayoutComponent } from './components/shared-layout/shared-layout.component';
import { SidebarTeamsComponent } from './components/side-bar-teams/side-bar-teams.component';
import { SheetsRailControlsComponent } from './components/sheets-rail-controls/sheets-rail-controls.component';
import { TaskingLayoutComponent } from './components/tasking-layout/tasking-layout.component';
import { BoardsEditorComponent } from '@modules/boards/components/editor/boards-editor.component';
import { BoardsLoadingComponent } from '@modules/boards/components/loading/boards-loading.component';
import { BoardsLayoutComponent } from '@modules/boards/components/layout/boards-layout.component';
import { BoardsRailControlsComponent } from '@modules/boards/components/layout/boards-rail-controls.component';
import { BoardsFolderPreviewComponent } from '@modules/boards/components/library/boards-folder-preview/boards-folder-preview.component';
import { BoardsListComponent } from '@modules/boards/components/library/boards-list/boards-list.component';
import { BoardsPreviewComponent } from '@modules/boards/components/library/boards-preview/boards-preview.component';
import { BoardsSidebarComponent } from '@modules/boards/components/library/boards-sidebar/boards-sidebar.component';
import { TooltipDirective } from '@modules/tooltip/tooltip.directive';
import { AccessLimitBannerComponent } from '@modules/settings/components/general/access-limit-banner/access-limit-banner.component';

@NgModule({
  imports: [
    CollapsibleRailShellModule,
    TeamItemLinkOpenerComponent,
    AccessLimitBannerComponent,
    TeamUpdatesEntryComponent,
    RailSkeletonComponent,
    WorkbookPreviewComponent,
    // Sheets' "Import spreadsheet" in the New Workbook menu (nav bar).
    ImportDialogComponent,
    // Sheets' "From a template" in the New Workbook menu (nav bar).
    TemplateChooserComponent,
    AdminSideBarComponent,
    AppCommonModule,
    AttachmentsModule,
    CalendarAppModule,
    CommonModule,
    ConnectionsModule,
    ContactsModule,
    DynamicPanelModule,
    ElementsModule,
    FilesModule,
    KnotsModule,
    MatIconModule,
    MessagesModule,
    NotesModule,
    SheetsModule,
    NotificationModule,
    OverlayModule,
    PagesRoutingModule,
    Button2Component,
    ContextMenuItemComponent,
    ContextMenuWindowComponent,
    IconComponent,
    PopoverDirective,
    SearchModule,
    SplitViewModule,
    TagsModule,
    TasksModule,
    UtilsModule,
    NavbarAiUsageRingComponent,
    SearchInputComponent,
    SearchPopupComponent,
    SidebarItemComponent,
    SettingsSideBarComponent,
    TooltipDirective,
    DateRangeCalendarEventsStoreProviderComponent,
    CalendarAppContextDirective,
    ContactsAppContextDirective,
    FilesAppContextDirective,
    MailAppContextDirective,
    NotesAppContextDirective,
    SheetsAppContextDirective,
    SheetsExpandedViewComponent,
    TaskingAppContextDirective,
    BaseNavBarComponent,
    SheetsRailControlsComponent,
    BoardsEditorComponent,
    BoardsRailControlsComponent,
    BoardsFolderPreviewComponent,
    BoardsListComponent,
    BoardsPreviewComponent,
    BoardsSidebarComponent,
    BoardsLoadingComponent,
    NavAddMenuComponent,
  ],
  declarations: [
    AdminLayoutComponent,
    BoardsLayoutComponent,
    ContactsLayoutComponent,
    EventsLayoutComponent,
    FilesLayoutComponent,
    LayoutComponent,
    MailLayoutComponent,
    NavBarComponent,
    RailShellComponent,
    NotesLayoutComponent,
    AtlasLayoutComponent,
    AtlasProtoPartComponent,
    SheetsLayoutComponent,
    SettingsLayoutComponent,
    SharedLayoutComponent,
    SidebarTeamsComponent,
    TaskingLayoutComponent,
  ],
  exports: [LayoutComponent],
})
export class PagesModule {}
